import React from 'react';
import { ArrowRightIcon, CheckIcon, CalendarIcon, UserIcon, TargetIcon, ClockIcon, BriefcaseIcon } from 'lucide-react';
import { PageTransition } from '../../components/layout/PageTransition';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { DecorativeShape } from '../../components/DecorativeShape';
import { Animate } from '../../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
export function EmbeddedPartner() {
  const {
    t
  } = useLanguage();
  const signs = t('embeddedPartner.signs') as string[];
  const roles = t('embeddedPartner.roles') as string[];
  const whatWeDoItems = t('embeddedPartner.whatWeDoItems') as string[];
  return <PageTransition data-id="element-1393">
      <div className="pt-12 pb-24" data-id="element-1394">
        {/* Hero */}
        <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-1395">
          <DecorativeShape type="blob" color="bg-blue-100/50" className="top-0 right-0 w-[500px] h-[500px] translate-x-1/4 -translate-y-1/4 -z-10" data-id="element-1396" />
          <Animate className="max-w-3xl" data-id="element-1397">
            <span className="inline-block px-4 py-2 bg-blue-50 text-blue-700 font-medium rounded-full text-sm mb-6" data-id="element-1398">
              {t('embeddedPartner.badge')}
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-1399">
              {t('embeddedPartner.title')}
              <br data-id="element-1400" />
              <span className="text-blue-600" data-id="element-1401">
                {t('embeddedPartner.titleHighlight')}
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-6 leading-relaxed" data-id="element-1402">
              {t('embeddedPartner.intro1')}
            </p>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-1403">
              {t('embeddedPartner.intro2')}
            </p>
            <Link to="/contact" data-id="element-1404">
              <Button size="lg" rightIcon={<ArrowRightIcon size={20} data-id="element-1406" />} data-id="element-1405">
                {t('embeddedPartner.heroCta')}
              </Button>
            </Link>
          </Animate>
        </section>

        {/* Practical details strip */}
        <section className="bg-blue-50 py-12 mb-20" data-id="element-1407">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1408">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1409">
              <Animate data-id="element-1410">
                <div className="flex items-start gap-4" data-id="element-1411">
                  <div className="p-3 bg-white rounded-xl shadow-sm flex-shrink-0" data-id="element-1412">
                    <ClockIcon size={24} className="text-blue-600" data-id="element-1413" />
                  </div>
                  <div data-id="element-1414">
                    <p className="font-bold text-charcoal mb-1" data-id="element-1415">
                      {t('embeddedPartner.detail1Title')}
                    </p>
                    <p className="text-gray-600 text-sm" data-id="element-1416">
                      {t('embeddedPartner.detail1Desc')}
                    </p>
                  </div>
                </div>
              </Animate>
              <Animate delay={0.1} data-id="element-1417">
                <div className="flex items-start gap-4" data-id="element-1418">
                  <div className="p-3 bg-white rounded-xl shadow-sm flex-shrink-0" data-id="element-1419">
                    <TargetIcon size={24} className="text-blue-600" data-id="element-1420" />
                  </div>
                  <div data-id="element-1421">
                    <p className="font-bold text-charcoal mb-1" data-id="element-1422">
                      {t('embeddedPartner.detail2Title')}
                    </p>
                    <p className="text-gray-600 text-sm" data-id="element-1423">
                      {t('embeddedPartner.detail2Desc')}
                    </p>
                  </div>
                </div>
              </Animate>
              <Animate delay={0.2} data-id="element-1424">
                <div className="flex items-start gap-4" data-id="element-1425">
                  <div className="p-3 bg-white rounded-xl shadow-sm flex-shrink-0" data-id="element-1426">
                    <BriefcaseIcon size={24} className="text-blue-600" data-id="element-1427" />
                  </div>
                  <div data-id="element-1428">
                    <p className="font-bold text-charcoal mb-1" data-id="element-1429">
                      {t('embeddedPartner.detail3Title')}
                    </p>
                    <p className="text-gray-600 text-sm" data-id="element-1430">
                      {t('embeddedPartner.detail3Desc')}
                    </p>
                  </div>
                </div>
              </Animate>
            </div>
          </div>
        </section>

        {/* Signs */}
        <section className="bg-cream py-20 mb-20" data-id="element-1431">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1432">
            <Animate className="mb-12" data-id="element-1433">
              <h2 className="text-3xl font-bold text-charcoal" data-id="element-1434">
                {t('embeddedPartner.signsTitle')}
              </h2>
            </Animate>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1435">
              {signs.map((sign, i) => <Animate key={i} delay={i * 0.05} data-id="element-1436">
                  <div className="flex items-start gap-3 bg-white rounded-2xl p-5 shadow-sm" data-id="element-1437">
                    <div className="mt-0.5 p-1 rounded-full bg-blue-50 text-blue-600 flex-shrink-0" data-id="element-1438">
                      <CheckIcon size={14} strokeWidth={3} data-id="element-1439" />
                    </div>
                    <span className="text-gray-700" data-id="element-1440">{sign}</span>
                  </div>
                </Animate>)}
            </div>
          </div>
        </section>

        {/* What I can be embedded as */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-1441">
          <Animate className="mb-10" data-id="element-1442">
            <h2 className="text-3xl font-bold text-charcoal mb-2" data-id="element-1443">
              {t('embeddedPartner.rolesTitle')}
            </h2>
            <p className="text-gray-500" data-id="element-1444">
              {t('embeddedPartner.rolesSubtitle')}
            </p>
          </Animate>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1445">
            {roles.map((role, i) => <Animate key={i} delay={i * 0.05} data-id="element-1446">
                <div className="flex items-start gap-3 bg-gray-50 rounded-2xl p-5" data-id="element-1447">
                  <div className="mt-0.5 p-1 rounded-full bg-blue-50 text-blue-600 flex-shrink-0" data-id="element-1448">
                    <UserIcon size={14} data-id="element-1449" />
                  </div>
                  <span className="text-gray-700 font-medium" data-id="element-1450">{role}</span>
                </div>
              </Animate>)}
          </div>
        </section>

        {/* How it works */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-1451">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start" data-id="element-1452">
            <Animate data-id="element-1453">
              <h2 className="text-3xl font-bold text-charcoal mb-6" data-id="element-1454">
                {t('embeddedPartner.whatWeDoTitle')}
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed" data-id="element-1455">
                {t('embeddedPartner.whatWeDoP1')}
              </p>
              <p className="text-lg text-gray-600 leading-relaxed" data-id="element-1456">
                {t('embeddedPartner.whatWeDoP2')}
              </p>
            </Animate>
            <Animate delay={0.1} data-id="element-1457">
              <div className="space-y-3" data-id="element-1458">
                {whatWeDoItems.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-1459">
                    <div className="mt-1 p-1 rounded-full bg-blue-50 text-blue-600 flex-shrink-0" data-id="element-1460">
                      <CheckIcon size={14} strokeWidth={3} data-id="element-1461" />
                    </div>
                    <span className="text-gray-700" data-id="element-1462">{item}</span>
                  </div>)}
              </div>
            </Animate>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 text-center" data-id="element-1463">
          <Animate data-id="element-1464">
            <Card className="p-12 bg-gradient-to-br from-blue-50 to-indigo-50 border-0" data-id="element-1465">
              <div className="w-16 h-16 mx-auto mb-6 bg-white rounded-full flex items-center justify-center shadow-md" data-id="element-1466">
                <CalendarIcon size={32} className="text-blue-600" data-id="element-1467" />
              </div>
              <h2 className="text-3xl font-bold mb-4" data-id="element-1468">
                {t('embeddedPartner.ctaTitle')}
              </h2>
              <p className="text-gray-600 mb-8 max-w-xl mx-auto" data-id="element-1469">
                {t('embeddedPartner.ctaDesc')}
              </p>
              <Link to="/contact" data-id="element-1470">
                <Button variant="primary" rightIcon={<ArrowRightIcon size={18} data-id="element-1472" />} data-id="element-1471">
                  {t('embeddedPartner.ctaCta')}
                </Button>
              </Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}